import React, { useState, useMemo } from "react";
import { BookOpen, Plus, Search, Filter, MoreVertical, Edit2, Ban, Trash2, CheckCircle2, Eye, PlayCircle, Star } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Button from "../../components/common/Button";
import StatCard from "../../components/common/StatCard";

const GlassCard = ({ children, className = "" }) => (
  <div className={`cht-bg-white/80 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}>
    {children}
  </div>
);

// --- Dummy Data ---
const DUMMY_COURSES = [
  { id: "CRS-101", title: "Modern Web Design Masterclass", instructor: "Sarah Jenkins", category: "Design", status: "Published", rating: 4.8, students: 1205, revenue: "₦450K" },
  { id: "CRS-102", title: "Advanced React Patterns", instructor: "David Osiota", category: "Development", status: "Under Review", rating: 0, students: 0, revenue: "₦0" },
  { id: "CRS-103", title: "Digital Marketing 101", instructor: "Linda Ng", category: "Marketing", status: "Published", rating: 4.5, students: 840, revenue: "₦210K" },
  { id: "CRS-104", title: "Data Science Fundamentals", instructor: "Michael Chen", category: "Data", status: "Draft", rating: 0, students: 0, revenue: "₦0" },
  { id: "CRS-105", title: "Framer Motion Animations", instructor: "Sarah Jenkins", category: "Design", status: "Published", rating: 4.9, students: 2150, revenue: "₦850K" },
  { id: "CRS-106", title: "Crypto Trading Secrets", instructor: "James Wilson", category: "Finance", status: "Suspended", rating: 2.1, students: 45, revenue: "₦15K" },
];

const AdminCourses = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("All Status");
  const [activeDropdown, setActiveDropdown] = useState(null);

  const filteredCourses = useMemo(() => {
    return DUMMY_COURSES.filter(course => {
      const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            course.instructor.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = filterStatus === "All Status" || course.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [searchQuery, filterStatus]);

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-700">
      <header className="cht-flex cht-flex-col md:cht-flex-row cht-items-start md:cht-items-center cht-justify-between cht-gap-4">
        <div>
          <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight">Global Course Library</h1>
          <p className="cht-text-gray-500 cht-font-medium">Oversight and approval workflows for all platform content.</p>
        </div>
        <Button variant="primary" leftIcon={<Plus size={18} />} className="cht-rounded-2xl">
          Create Global Course
        </Button>
      </header>

      {/* KPI Grid */}
      <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 lg:cht-grid-cols-4 cht-gap-6">
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-blue-50 cht-text-blue-600 cht-rounded-2xl"><BookOpen size={24} /></div>
          <div><p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Total Courses</p><h4 className="cht-text-2xl cht-font-black">842</h4></div>
        </GlassCard>
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-emerald-50 cht-text-emerald-600 cht-rounded-2xl"><CheckCircle2 size={24} /></div>
          <div><p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Published</p><h4 className="cht-text-2xl cht-font-black">715</h4></div>
        </GlassCard>
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-amber-50 cht-text-amber-600 cht-rounded-2xl"><Eye size={24} /></div>
          <div><p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Under Review</p><h4 className="cht-text-2xl cht-font-black">18</h4></div>
        </GlassCard>
        <GlassCard className="cht-p-6 cht-rounded-[28px] cht-flex cht-items-center cht-gap-4">
          <div className="cht-p-4 cht-bg-purple-50 cht-text-purple-600 cht-rounded-2xl"><PlayCircle size={24} /></div>
          <div><p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Total Enrollments</p><h4 className="cht-text-2xl cht-font-black">45.2K</h4></div>
        </GlassCard>
      </div>

      {/* Filter Bar */}
      <div className="cht-flex cht-flex-col md:cht-flex-row cht-items-center cht-gap-4 cht-bg-white cht-p-4 cht-rounded-[24px] cht-border cht-border-gray-50 cht-shadow-sm">
        <div className="cht-relative cht-flex-1 cht-w-full">
          <Search className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400" size={18} />
          <input 
            type="text" 
            placeholder="Search by course title or instructor..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-xl cht-py-3.5 cht-pl-12 cht-pr-4 cht-text-sm cht-font-bold focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all"
          />
        </div>
        <div className="cht-flex cht-items-center cht-gap-2 cht-w-full md:cht-w-auto">
          {["All Status", "Published", "Under Review", "Draft", "Suspended"].map(status => (
            <button
              key={status}
              onClick={() => setFilterStatus(status)}
              className={`cht-flex-1 md:cht-flex-none cht-px-4 cht-py-2.5 cht-rounded-xl cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest cht-transition-all ${
                filterStatus === status ? "cht-bg-[#1e293b] cht-text-white" : "cht-bg-gray-50 cht-text-gray-400 hover:cht-bg-gray-100"
              }`}
            >
              {status}
            </button>
          ))}
        </div>
      </div>

      {/* Courses Table */}
      <GlassCard className="cht-rounded-[40px] cht-overflow-visible">
        <div className="cht-overflow-x-auto">
          <table className="cht-w-full">
            <thead>
              <tr className="cht-border-b cht-border-gray-50">
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Course & Instructor</th>
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Category</th>
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Status</th>
                <th className="cht-px-8 cht-py-6 cht-text-left cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Performance</th>
                <th className="cht-px-8 cht-py-6 cht-text-right cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Actions</th>
              </tr>
            </thead>
            <tbody className="cht-divide-y cht-divide-gray-50">
              {filteredCourses.map((course) => (
                <tr key={course.id} className="hover:cht-bg-gray-50/50 cht-transition-colors group">
                  <td className="cht-px-8 cht-py-6">
                    <div className="cht-space-y-1">
                      <h4 className="cht-text-sm cht-font-black cht-text-gray-900 cht-line-clamp-1">{course.title}</h4>
                      <p className="cht-text-xs cht-text-gray-400 cht-font-medium">by {course.instructor}</p>
                    </div>
                  </td>
                  <td className="cht-px-8 cht-py-6">
                    <span className="cht-px-3 cht-py-1.5 cht-bg-gray-50 cht-text-gray-600 cht-rounded-xl cht-text-[10px] cht-font-black cht-uppercase cht-tracking-widest">
                      {course.category}
                    </span>
                  </td>
                  <td className="cht-px-8 cht-py-6">
                    <span className={`cht-px-3 cht-py-1.5 cht-rounded-full cht-text-[9px] cht-font-black cht-uppercase cht-tracking-widest ${
                      course.status === 'Published' ? 'cht-bg-emerald-100 cht-text-emerald-700' :
                      course.status === 'Under Review' ? 'cht-bg-amber-100 cht-text-amber-700' :
                      course.status === 'Suspended' ? 'cht-bg-rose-100 cht-text-rose-700' :
                      'cht-bg-gray-100 cht-text-gray-600'
                    }`}>
                      {course.status}
                    </span>
                  </td>
                  <td className="cht-px-8 cht-py-6">
                    <div className="cht-space-y-1">
                      <div className="cht-flex cht-items-center cht-gap-1 cht-text-xs cht-font-black cht-text-amber-500">
                        <Star size={12} className="fill-amber-500" /> {course.rating > 0 ? course.rating : "N/A"}
                      </div>
                      <p className="cht-text-[10px] cht-text-gray-400 cht-font-bold">{course.students} Enrolled</p>
                    </div>
                  </td>
                  <td className="cht-px-8 cht-py-6 cht-text-right">
                    <div className="cht-relative cht-inline-block">
                      <button 
                        onClick={() => setActiveDropdown(activeDropdown === course.id ? null : course.id)}
                        className="cht-w-10 cht-h-10 cht-flex cht-items-center cht-justify-center cht-rounded-xl cht-text-gray-400 hover:cht-bg-gray-100 hover:cht-text-gray-900 transition-colors"
                      >
                        <MoreVertical size={20} />
                      </button>
                      <AnimatePresence>
                        {activeDropdown === course.id && (
                          <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            className="cht-absolute cht-right-0 cht-mt-2 cht-w-48 cht-bg-white cht-rounded-2xl cht-shadow-[0_20px_60px_rgba(0,0,0,0.1)] cht-border cht-border-gray-50 cht-p-2 cht-z-[100]"
                          >
                            <button className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-gray-700 hover:cht-bg-gray-50 cht-rounded-xl transition-colors">
                              <Eye size={16} /> View Course
                            </button>
                            {course.status === 'Under Review' && (
                              <button className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-emerald-600 hover:cht-bg-emerald-50 cht-rounded-xl transition-colors">
                                <CheckCircle2 size={16} /> Approve Publish
                              </button>
                            )}
                            <div className="cht-h-px cht-bg-gray-100 cht-my-1" />
                            {course.status !== 'Suspended' ? (
                              <button className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-amber-600 hover:cht-bg-amber-50 cht-rounded-xl transition-colors">
                                <Ban size={16} /> Suspend Course
                              </button>
                            ) : (
                              <button className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-blue-600 hover:cht-bg-blue-50 cht-rounded-xl transition-colors">
                                <CheckCircle2 size={16} /> Restore Course
                              </button>
                            )}
                            <button className="cht-w-full cht-flex cht-items-center cht-gap-3 cht-px-4 cht-py-2.5 cht-text-xs cht-font-bold cht-text-rose-600 hover:cht-bg-rose-50 cht-rounded-xl transition-colors">
                              <Trash2 size={16} /> Delete Forever
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredCourses.length === 0 && (
            <div className="cht-py-20 cht-text-center">
              <div className="cht-w-16 cht-h-16 cht-bg-gray-50 cht-rounded-2xl cht-flex cht-items-center cht-justify-center cht-mx-auto cht-mb-4 cht-text-gray-300">
                <BookOpen size={32} />
              </div>
              <h3 className="cht-text-lg cht-font-black cht-text-gray-900">No courses found</h3>
              <p className="cht-text-sm cht-text-gray-400 cht-font-medium">Try adjusting your filters or search terms.</p>
            </div>
          )}
        </div>
      </GlassCard>

      {activeDropdown && (
        <div 
          className="cht-fixed cht-inset-0 cht-z-[50]" 
          onClick={() => setActiveDropdown(null)} 
        />
      )}
    </div>
  );
};

export default AdminCourses;
